import { invariant } from '@seen/core/invariant'
import * as questionnaires from '@seen/core/models/questionnaires/index'
import { ValidatedTool } from '@seen/core/models/validatedTool'
import { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import { recordKeys } from '@seen/core/utils'
import { Badge } from '@seen/ui'
import type { JSX } from 'react'
import { z } from 'zod'
import { FormSubmitButton } from '#src/components/core/form/FormSubmitButton.tsx'
import { useValidatedToolForm } from '#src/components/validated-tool-form/shared/useValidatedToolForm.ts'
import { ValidatedToolField } from '#src/components/validated-tool-form/shared/ValidatedToolField.tsx'
import {
  ValidatedToolInnerForm,
  type ValidatedToolInnerFormProps,
} from '#src/components/validated-tool-form/shared/ValidatedToolInnerForm.tsx'
import { ValidatedToolSection } from '#src/components/validated-tool-form/shared/ValidatedToolSection.tsx'
import type {
  ValidatedToolFieldSuggestion,
  ValidatedToolSetValue,
} from '#src/components/validated-tool-form/shared/validatedToolSuggestion.ts'
import type { UseFormReturn } from '#src/hooks/useForm.ts'

export interface PHQ2FormProps extends Omit<
  ValidatedToolInnerFormProps,
  'validatedTool' | 'form' | 'subtitle'
> {
  response?: ValidatedToolResponse
  /* AI-staged answers to pre-populate an unsubmitted form. */
  prefillValuesByLinkId?: Record<string, string>
  /* Per-field AI suggestion overlay, keyed by linkId. */
  renderFieldSuggestions?: (
    setValue: ValidatedToolSetValue
  ) => Record<string, ValidatedToolFieldSuggestion>
  restorationId?: string
}

const schema = z.object({
  '/44250-9': z.string().nullable(),
  '/44255-8': z.string().nullable(),
  '/55758-7': z.number().nullable(),
})

const validatedTool = new ValidatedTool(questionnaires.phq2Questionnaire)

export function PHQ2Form({
  response,
  prefillValuesByLinkId,
  renderFieldSuggestions,
  restorationId,
  ...delegated
}: PHQ2FormProps): JSX.Element {
  const form = useValidatedToolForm({
    schema,
    validatedTool,
    response,
    prefillValuesByLinkId,
    restorationId,
  })
  const fieldSuggestions = renderFieldSuggestions?.(form.setSuggestionValue)

  return (
    <ValidatedToolInnerForm
      validatedTool={validatedTool}
      form={form}
      response={response}
      {...delegated}
    >
      <ValidatedToolSection title="Over the last 2 weeks, how often have you been bothered by any of the following problems?">
        {recordKeys(schema.shape).map((linkId) => (
          <ValidatedToolField
            key={linkId}
            control={form.control}
            name={linkId}
            validatedTool={validatedTool}
            suggestion={fieldSuggestions?.[linkId]}
          />
        ))}
      </ValidatedToolSection>

      <Score form={form} validatedTool={validatedTool} response={response} />

      {!response && (
        <div>
          <FormSubmitButton control={form.control}>Submit</FormSubmitButton>
        </div>
      )}
    </ValidatedToolInnerForm>
  )
}

const COLORS = {
  'Screen Positive (Major Depressive Disorder Likely)': 'red',
  'Screen Negative': 'green',
} as const satisfies Record<questionnaires.Phq2Category, string>

function Score({
  form,
  validatedTool,
  response,
}: {
  form: UseFormReturn<any>
  validatedTool: ValidatedTool
  response?: ValidatedToolResponse
}): JSX.Element {
  const valuesByLinkId = form.watch()
  const result = response
    ? response
    : ValidatedToolResponse.buildSafe(validatedTool, { valuesByLinkId })

  const label = questionnaires.phq2Category(result.score)
  invariant(label, `PHQ-2 score ${result.score} has no category`)
  const color = COLORS[label]

  return (
    <div className="flex items-center gap-2">
      <input
        type="hidden"
        {...form.register('/55758-7', {
          value: result.score,
        })}
      />
      <h2>
        Score: <b>{result.score}</b>
      </h2>
      <Badge color={color} className="mt-px">
        {label}
      </Badge>
    </div>
  )
}
